<template>
  <section class="ni-section ni-int" id="int-communication">
    <div class="ni-container">
      <SectionHeader
        variant="dark"
        :subtitle="t('newIndex.common.worldSubtitle')"
        more-href="/publish"
      >
        <template #title>
          <span class="ni-section-header__title-mark">
            {{ t('newIndex.sections.intComm.title') }}
          </span>
        </template>
      </SectionHeader>

      <div class="ni-int__toolbar">
        <div class="ni-int__toolbar-left">
          <div class="ni-int__cats">
            <div class="ni-int__cat-accent" aria-hidden="true">›</div>
            <div class="ni-int__cat-switch">
            <button
              id="int-cat-world-in-qd"
              type="button"
              class="ni-int__cat-btn"
              :class="{ 'ni-int__cat-btn--active': activeCategory === 'worldInQd' }"
              @click="handleCategorySwitch('worldInQd')"
            >
              {{ t('newIndex.sections.intComm.worldInQd') }}
            </button>
            <span class="ni-int__cat-sep">/</span>
            <button
              id="int-cat-qd-in-world"
              type="button"
              class="ni-int__cat-btn"
              :class="{ 'ni-int__cat-btn--active': activeCategory === 'qdInWorld' }"
              @click="handleCategorySwitch('qdInWorld')"
            >
              {{ t('newIndex.sections.intComm.qdInWorld') }}
            </button>
          </div>
          </div>
        </div>

        <div class="ni-int__filters">
          <template v-for="(lbl, idx) in filterTabs" :key="lbl">
            <span v-if="idx > 0" class="ni-int__filter-sep">/</span>
            <button
              :id="`int-filter-btn-${idx}`"
              type="button"
              class="ni-int__filter-btn"
              :class="{ 'ni-int__filter-btn--active': activeFilterIdx === idx }"
              @click="activeFilterIdx = idx"
            >
              {{ lbl }}
            </button>
          </template>
        </div>
      </div>

      <div class="ni-int__grid">
        <div class="ni-int__video">
          <div class="ni-int__video-bg">
            <Transition name="ni-int-fade" mode="out-in">
              <img
                :key="activeMedia.id"
                :src="videoBackdropUrl"
                :alt="activeMedia.title"
              />
            </Transition>
            <div class="ni-int__video-vignette"></div>
          </div>

          <div class="ni-int__video-info">
            <Transition name="ni-int-fade" mode="out-in">
              <div :key="activeMedia.id" class="ni-int__video-copy">
                <template v-if="activeMedia.id === 'int-1'">
                  <h3 class="ni-int__video-title">{{ t('newIndex.sections.intComm.heroTitle') }}</h3>
                  <p class="ni-int__video-summary ni-int__video-summary--hero">
                    {{ t('newIndex.sections.intComm.ready') }}
                  </p>
                </template>
                <template v-else>
                  <h3 class="ni-int__video-title">{{ activeMedia.title }}</h3>
                  <p class="ni-int__video-summary">{{ activeMedia.summary }}</p>
                </template>
              </div>
            </Transition>
          </div>

          <div class="ni-int__controls">
            <button
              id="int-play-button-controller"
              type="button"
              @click="togglePlay"
            >
              <span v-if="isPlaying" aria-hidden="true">❚❚</span>
              <span v-else aria-hidden="true">▶</span>
            </button>
            <span>
              {{ isPlaying ? `0:${elapsedSeconds}` : '0:00' }}
            </span>
            <div class="ni-int__progress">
              <div
                class="ni-int__progress-bar"
                :style="{ width: `${videoProgress}%` }"
              ></div>
            </div>
            <span class="ni-int__duration">{{ activeMedia.videoDuration }}</span>
          </div>
        </div>

        <div class="ni-int__cards">
          <article
            v-for="(item, index) in currentItems"
            :id="`int-right-list-card-${item.id}`"
            :key="item.id"
            class="ni-int__card"
            :class="{ 'ni-int__card--active': index === activeIdx }"
            @click="handleCardClick(index)"
          >
            <div class="ni-int__card-thumb">
              <img :src="item.imageUrl" :alt="item.title" />
            </div>
            <div class="ni-int__card-body">
              <span class="ni-int__card-tag">{{ item.tag }}</span>
              <h4 class="ni-int__card-title">{{ item.title }}</h4>
              <p class="ni-int__card-summary">{{ item.summary }}</p>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getWorldInQingdaoItems,
  getQingdaoInWorldItems
} from '@/data/newIndexMock.js';
import SectionHeader from './shared/SectionHeader.vue';

const { t, tm } = useI18n();

const INT1_HERO_IMAGE =
  'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1200&h=800&q=80';

const activeCategory = ref('worldInQd');
const activeFilterIdx = ref(0);
const activeIdx = ref(0);
const isPlaying = ref(false);
const videoProgress = ref(0);
let progressTimer = null;

const filterTabs = computed(() => {
  const filters = tm('newIndex.sections.intComm.filters');
  return Array.isArray(filters) ? filters : [];
});

const worldInQingdaoItems = computed(() => getWorldInQingdaoItems());
const qingdaoInWorldItems = computed(() => getQingdaoInWorldItems());

const currentItems = computed(() =>
  activeCategory.value === 'worldInQd'
    ? worldInQingdaoItems.value
    : qingdaoInWorldItems.value
);

const activeMedia = computed(
  () => currentItems.value[activeIdx.value] || currentItems.value[0]
);

const videoBackdropUrl = computed(() =>
  activeMedia.value?.id === 'int-1' ? INT1_HERO_IMAGE : activeMedia.value?.imageUrl
);

const elapsedSeconds = computed(() =>
  Math.floor((videoProgress.value / 100) * 15)
    .toString()
    .padStart(2, '0')
);

function stopProgressTimer() {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
}

function startProgressTimer() {
  stopProgressTimer();
  progressTimer = setInterval(() => {
    if (videoProgress.value >= 100) {
      isPlaying.value = false;
      videoProgress.value = 0;
      stopProgressTimer();
      return;
    }
    videoProgress.value += 1;
  }, 300);
}

function togglePlay() {
  isPlaying.value = !isPlaying.value;
}

function handleCardClick(index) {
  activeIdx.value = index;
  isPlaying.value = true;
  videoProgress.value = 0;
}

function handleCategorySwitch(category) {
  activeCategory.value = category;
  activeIdx.value = 0;
  isPlaying.value = false;
  videoProgress.value = 0;
}

watch(isPlaying, (playing) => {
  if (playing) {
    startProgressTimer();
  } else {
    stopProgressTimer();
  }
});

onBeforeUnmount(() => {
  stopProgressTimer();
});
</script>

